import { apiFetch } from './client';

// Typed client for the calling / RSVP endpoints
// (internal/api/callings.go). A calling (Swedish: kallelse) invites the
// members on an activity's roster to respond yes / no / maybe.

// Opt-out roster model: a calling implicitly calls the whole team roster.
// 'invited' = called, awaiting reply; 'excluded' = blocked (not called).
export type CallingResponseStatus =
  | 'invited'
  | 'attending'
  | 'declined'
  | 'maybe'
  | 'excluded';
export type CallingStatus = 'open' | 'closed';

const orgPath = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}`;

export interface CreateCallingInput {
  activity_id: string;
  title: string;
  message?: string;
  // RFC3339 or omitted (no deadline).
  respond_by?: string;
  // Members on the activity's teams to block from this calling (opt-out model).
  // Everyone else on the roster — including members added later — is called.
  excluded_member_ids?: string[];
}

export interface CreateCallingResponse {
  id: string;
}

export async function createCalling(
  token: string | null,
  slug: string,
  input: CreateCallingInput,
): Promise<CreateCallingResponse> {
  return apiFetch<CreateCallingResponse>(`${orgPath(slug)}/callings`, {
    method: 'POST',
    body: input,
    token,
  });
}

export async function closeCalling(
  token: string | null,
  slug: string,
  callingID: string,
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/callings/${encodeURIComponent(callingID)}/close`, {
    method: 'POST',
    token,
  });
}

// setCallingExclusions replaces the blocked-member set for a calling (opt-out
// model). Pass the full desired excluded set; members not listed are called.
export async function setCallingExclusions(
  token: string | null,
  slug: string,
  callingID: string,
  excludedMemberIDs: string[],
): Promise<void> {
  await apiFetch<void>(
    `${orgPath(slug)}/callings/${encodeURIComponent(callingID)}/exclusions`,
    { method: 'POST', body: { excluded_member_ids: excludedMemberIDs }, token },
  );
}

export interface CallingSummary {
  calling_id: string;
  title: string;
  status: CallingStatus;
  attending: number;
  declined: number;
  maybe: number;
  invited: number;
  total: number;
}

export async function getCallingSummary(
  token: string | null,
  slug: string,
  callingID: string,
): Promise<CallingSummary> {
  return apiFetch<CallingSummary>(
    `${orgPath(slug)}/callings/${encodeURIComponent(callingID)}/summary`,
    { token },
  );
}

export interface RespondCallingInput {
  member_id: string;
  status: 'attending' | 'declined' | 'maybe';
  note?: string;
}

export async function respondCalling(
  token: string | null,
  slug: string,
  callingID: string,
  input: RespondCallingInput,
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/callings/${encodeURIComponent(callingID)}/respond`, {
    method: 'POST',
    body: input,
    token,
  });
}

export interface MyCalling {
  calling_id: string;
  invitation_id: string;
  member_id: string;
  activity_id: string;
  activity_title: string;
  activity_start: string;
  title: string;
  message: string;
  respond_by: string | null;
  my_status: CallingResponseStatus;
  calling_status: CallingStatus;
}

export interface ListMyCallingsResponse {
  callings: MyCalling[];
}

export async function listMyCallings(
  token: string | null,
  slug: string,
  memberID: string,
  includeAnswered = false,
): Promise<ListMyCallingsResponse> {
  const qs = includeAnswered ? '?include_answered=true' : '';
  return apiFetch<ListMyCallingsResponse>(
    `${orgPath(slug)}/members/${encodeURIComponent(memberID)}/callings${qs}`,
    { token },
  );
}

// --- activity detail: calling + per-member roster ---

export interface ActivityCallingInfo {
  id: string;
  title: string;
  message: string;
  status: CallingStatus;
  respond_by: string | null;
}

export interface ActivityInvitation {
  invitation_id: string;
  member_id: string;
  name: string;
  status: CallingResponseStatus;
  responded_at: string | null;
}

export interface ActivityCallingResponse {
  calling: ActivityCallingInfo | null;
  invitations: ActivityInvitation[];
  // Set when no calling exists yet but one is queued to auto-send.
  scheduled_send_at: string | null;
  scheduled_lead_days: number | null;
}

// listInvitationsForActivity returns the activity's latest calling (or null)
// plus the per-member RSVP roster. Leader-of-the-activity / org-admin scope.
export async function listInvitationsForActivity(
  token: string | null,
  slug: string,
  activityID: string,
): Promise<ActivityCallingResponse> {
  return apiFetch<ActivityCallingResponse>(
    `${orgPath(slug)}/activities/${encodeURIComponent(activityID)}/calling`,
    { token },
  );
}
